<template>
  <div
    class="tag-input form-control"
    :class="[
      `form-control-${size}`,
      `text-${size}`,
      {
        'has-error': hasError,
        'read-only': readonly,
        'is-focused': isFocused
      }
    ]"
    @click="focusInput"
  >
    <Chip
      v-for="(tag, index) in modelValue"
      :key="`${tag}-${index}`"
      :label="tag"
      size="sm"
      :can-remove="!readonly"
      @click.stop
      @remove-chip="removeTag(index)"
    />
    <input
      :id="id"
      ref="inputRef"
      v-model="draft"
      type="text"
      autocomplete="off"
      class="tag-input__field"
      :placeholder="modelValue.length === 0 ? placeholder : ''"
      :readonly="readonly"
      :disabled="readonly"
      :aria-label="placeholder"
      @keydown="onKeydown"
      @paste="onPaste"
      @focus="isFocused = true"
      @blur="onBlur"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

// components
import Chip from '@/components/chip/Chip.vue'
// utilities
import { addTagsToList, parseTagDraft, removeTagAtIndex } from '@/utilities/tagInput'

const props = withDefaults(
  defineProps<{
    /** Assigned tag names */
    modelValue?: string[]
    /** Shown when no tags are assigned */
    placeholder?: string
    /** Native id on the inner input */
    id?: string | null
    /** Error state on the control */
    hasError?: boolean
    /** Prevent adding or removing tags */
    readonly?: boolean
    /** Form control size */
    size?: string
  }>(),
  {
    modelValue: () => [],
    placeholder: undefined,
    id: null,
    hasError: false,
    readonly: false,
    size: 'md'
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: string[]]
  change: [value: string[]]
}>()

/** Text currently being typed */
const draft = ref('')
/** Inner text field, focused when the control is clicked */
const inputRef = ref<HTMLInputElement | null>(null)
/** Whether the inner input is focused, used for the focus ring */
const isFocused = ref(false)

/**
 * Focus the text field so typing can start from anywhere on the control.
 */
function focusInput(): void {
  if (props.readonly) {
    return
  }

  inputRef.value?.focus()
}

/**
 * Commit the draft as one or more tags and clear the input.
 */
function commitDraft(): void {
  const next = addTagsToList(props.modelValue, parseTagDraft(draft.value))
  draft.value = ''
  emitTags(next)
}

/**
 * Emit the updated tag list.
 *
 * @param tags - Tags to persist on the parent
 */
function emitTags(tags: string[]): void {
  emit('update:modelValue', tags)
  emit('change', tags)
}

/**
 * Remove a chip by index.
 *
 * @param index - Chip index in the current list
 */
function removeTag(index: number): void {
  if (props.readonly) {
    return
  }

  emitTags(removeTagAtIndex(props.modelValue, index))
}

/**
 * Handle Enter / comma to create a chip, and Backspace to remove the last chip.
 *
 * @param event - Keyboard event from the inner input
 */
function onKeydown(event: KeyboardEvent): void {
  if (props.readonly) {
    return
  }

  // Enter or comma commits the typed name as a chip
  if (event.key === 'Enter' || event.key === ',') {
    event.preventDefault()
    commitDraft()
    return
  }

  // Backspace on an empty field removes the last chip
  if (event.key === 'Backspace' && draft.value === '' && props.modelValue.length > 0) {
    event.preventDefault()
    removeTag(props.modelValue.length - 1)
  }
}

/**
 * Split pasted comma-separated text into chips.
 *
 * @param event - Clipboard paste event
 */
function onPaste(event: ClipboardEvent): void {
  if (props.readonly) {
    return
  }

  const pasted = event.clipboardData?.getData('text') ?? ''

  // Only intercept pastes that contain separators so a single name still types normally
  if (!pasted || !/[,;\n]/.test(pasted)) {
    return
  }

  event.preventDefault()
  emitTags(addTagsToList(props.modelValue, parseTagDraft(`${draft.value} ${pasted}`)))
  draft.value = ''
}

/**
 * Commit any remaining draft when the field blurs so typed text is not lost.
 */
function onBlur(): void {
  isFocused.value = false

  if (draft.value.trim() !== '') {
    commitDraft()
  }
}
</script>

<style scoped>
.tag-input {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: var(--s-1);
  overflow-x: auto;
  overflow-y: hidden;
  cursor: text;
}

.tag-input.read-only {
  cursor: not-allowed;
}

.tag-input.is-focused:not(.read-only) {
  border: 1px solid var(--border-brand);
  box-shadow: var(--focus-ring-brand);
}

.tag-input :deep(.chip) {
  flex-shrink: 0;
  line-height: var(--text-xs-lh);
}

.tag-input__field {
  flex: 1 1 4rem;
  min-width: 4rem;
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  line-height: inherit;
  outline: none;
}

.tag-input__field::placeholder {
  color: var(--text-placeholder);
}
</style>
